import { Column } from 'primereact/column';
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import API from '../../utils/ApiConfig';
import { ALL_JOB_STATUSES, GET_FAILED_JOB_REPORT, JOBS } from '../../utils/ApiEndpoints';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import DialogComponent from '../common/Dialog/DialogComponent';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import Loader from '../common/Loader/Loader';
import JobStatusProgressBar from './JobStatusProgressBar';

const Jobs = props => {
  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`jobs:${e}`, data) : t(`jobs:${e}`));
  const tc_common = (e, data) => (data ? t(`common:${e}`, data) : t(`common:${e}`));

  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [detailsMessage, setDetailsMessage] = useState('');
  const [showDetailsMessage, setShowDetailsMessage] = useState(false);
  const [isFailureMessage, setFailureMessage] = useState(true);

  const [isLoading, setLoading] = useState(true);
  const [isTableLoading, setTableLoading] = useState(true);

  const [statusSelected, setStatusSelected] = useState('');
  const [statuses, setStatuses] = useState([]);

  const [onlyMine, setOnlyMine] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');

  const getStatuses = () => API.get(ALL_JOB_STATUSES);
  const getJobs = () => API.get(JOBS(statusSelected, onlyMine), {});

  const getUsers = () => API.get(JOBS('', false), {});

  const getFailedJobReport = jobId => API.get(GET_FAILED_JOB_REPORT(jobId));

  const closeErrorMessage = () => setShowErrorMessage(false);
  const closeDetailsMessage = () => setShowDetailsMessage(false);

  const CommonService = props.services.CommonService;
  const { $compile, $scope } = props.services;
  const pageHeaderRef = useRef(null);
  const isRedesignEnabled = () => !!$scope?.$root?.isLegacyThemeRedesignEnabled?.();

  const handleChangeStatus = event => setStatusSelected(event.target.value);

  const handleChangeUser = event => {
    setSelectedUser(event.target.value);
  };

  useEffect(() => {
    getUsers().then(response => {
      const uniqueUsers = [];
      const userIds = new Set();

      response.data.forEach(job => {
        if (!userIds.has(job.initiatedByUserId)) {
          userIds.add(job.initiatedByUserId);
          uniqueUsers.push({
            name: job.initiatedByUser,
            id: job.initiatedByUserId,
          });
        }
      });

      setUsers(uniqueUsers);
    });
  }, []);

  useEffect(() => {
    function fetchData() {
      return Promise.all([getStatuses(), getJobs()])
        .then(([statuses, jobs]) => {
          statuses.data && setStatuses(statuses.data);
          handleJobsData(jobs.data);
        })
        .catch(err => {
          err && err.response && err.response.data && setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        })
        .finally(() => {
          setLoading(false);
          setTableLoading(false);
        });
    }

    fetchData();

    const refreshJobStatusData = setInterval(() => {
      setTableLoading(true);
      fetchData(); // Fetch data every 30 seconds
    }, 30 * 1000);

    return () => clearInterval(refreshJobStatusData);
  }, []);

  useEffect(() => {
    if (!isRedesignEnabled() || !$compile || !$scope || !pageHeaderRef.current) {
      return;
    }

    $scope.pageHeaderTitle = CommonService.isDeleteBudgetVersionEnabled ? tc('JOBS_TITLE') : tc('HEADER_TITLE');
    if (!$scope.$$phase) {
      $scope.$apply();
    }

    const pageHeaderElement = $compile('<lmapp-page-header icon="icon-job-status" [title]="pageHeaderTitle"></lmapp-page-header>')($scope);
    pageHeaderRef.current.appendChild(pageHeaderElement[0]);

    return () => {
      pageHeaderElement.remove();
    };
  }, []);

  const handleSearch = () => {
    setTableLoading(true);
    getJobs()
      .then(response => {
        let filteredData = response.data;
        // Filter by selected user after fetching the jobs
        if (selectedUser !== 'all') {
          filteredData = filteredData.filter(job => job.initiatedByUserId === selectedUser);
        }
        handleJobsData(filteredData);
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status);
        setShowErrorMessage(true);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const handleJobsData = data => {
    if (data) {
      let currentTime = new Date();

      for (const row of data) {
        row.startTimeDate = moment.utc(row.startTime).toDate();
        row.startTimeText = moment(row.startTimeDate).format('MM/DD/YYYY hh:mm:ss A');
        if (row.jobStatus === 'RUNNING' || row.jobStatus === 'PENDING') {
          row.endTime = currentTime.getTime();
          row.endTimeDate = currentTime;
          row.endTimeText = tc('JOBS_GRID_DATA_PENDING');

          row.duration = Infinity;
          row.durationText = tc('JOBS_GRID_DATA_PENDING');
        } else {
          row.endTimeDate = moment.utc(row.endTime).toDate();
          row.endTimeText = moment(row.endTimeDate).format('MM/DD/YYYY hh:mm:ss A');

          row.durationText = Math.floor(row.duration / 1000 / 3600) + ':' + moment.utc(row.duration).format('mm:ss');
        }
      }
      setJobs(data.filter(x => x?.jobType !== 'DATA_COLLECTION_MIGRATION'));
    } else {
      setJobs([]);
    }
  };

  const handleDownload = id => {
    getFailedJobReport(id)
      .then(response => {
        var downloadName = response.headers['content-disposition'].split(';')[1].trim().split('=')[1].replace(/"/g, '');
        const downloadLinkHiddenEl = document.createElement('a');
        const rawFileData = response.data;
        const blob = new Blob([rawFileData], { type: 'text/plain' });
        const fileURL = URL.createObjectURL(blob);
        downloadLinkHiddenEl.href = fileURL;
        downloadLinkHiddenEl.download = downloadName;
        downloadLinkHiddenEl.target = '_blank';
        downloadLinkHiddenEl.click();
      })
      .catch(error => console.error('Failed to download the job report:', error));
  };

  const openDetailsMessage = (detailsBody, isFailureMessage) => {
    setDetailsMessage(detailsBody);
    setFailureMessage(isFailureMessage);
    setShowDetailsMessage(true);
  };

  const columnNames = [
    {
      field: 'jobTypeName',
      header: tc('JOBS_GRID_HEADER_JOB_TYPE'),
      style: { minWidth: '110px' },
    },
    {
      field: 'profileName',
      header: tc('JOBS_GRID_HEADER_PROFILE'),
      style: { minWidth: '100px' },
    },
    {
      field: 'planName',
      header: tc('JOBS_GRID_HEADER_PLAN'),
      style: { minWidth: '100px' },
    },
    {
      field: 'initiatedByUser',
      header: tc('JOBS_GRID_HEADER_USER'),
      style: { minWidth: '100px' },
    },
    {
      field: 'startTimeText',
      header: tc('JOBS_GRID_HEADER_START_TIME'),
      sortField: 'startTime',
      style: { minWidth: '160px' },
    },
    {
      field: 'endTimeText',
      header: tc('JOBS_GRID_HEADER_END_TIME'),
      sortField: 'endTime',
      style: { minWidth: '160px' },
    },
    {
      field: 'durationText',
      header: tc('JOBS_GRID_HEADER_DURATION'),
      sortField: 'duration',
      style: { minWidth: '100px' },
    },
  ];

  const columns = columnNames.map(({ field, header, bodyClassName, sortField, style }, index) => {
    return (
      <Column
        key={index}
        field={field}
        header={header}
        style={style}
        bodyClassName={bodyClassName}
        sortField={sortField ? sortField : field}
        sortable
      />
    );
  });

  const rowClass = rowData => {
    return {
      'jobs-grid-running': rowData.jobStatus === 'RUNNING' || rowData.jobStatus === 'PENDING',
      'jobs-grid-completed': rowData.jobStatus === 'COMPLETED',
      'jobs-grid-failed': rowData.jobStatus === 'FAILED' || rowData.jobStatus === 'FAILED_WITH_REPORT',
    };
  };

  const actionTemplate = rowData => {
    return (
      <div className="action-icon">
        {rowData.jobStatus === 'FAILED_WITH_REPORT' && <i className="icon-download_file" onClick={() => handleDownload(rowData.id)}></i>}
        {rowData.detailsBody !== undefined && rowData.detailsBody !== null && (
          <img
            className="icon-show_details"
            src="src/assets/images/icons/show-details-icon.svg"
            alt="Show Details"
            onClick={() =>
              openDetailsMessage(
                '<span>' + rowData.detailsBody + '</span>',
                rowData.jobStatus === 'FAILED' || rowData.jobStatus === 'FAILED_WITH_REPORT'
              )
            }
          />
        )}
      </div>
    );
  };

  const staticColumns = (
    <Column
      body={actionTemplate}
      header={
        <div className="action-column-header">
          <span>Actions</span>
        </div>
      }
    />
  );

  const progressBarJobStatusTemplate = rowData => {
    return (
      <JobStatusProgressBar
        jobType={rowData.jobType}
        jobStatus={rowData.jobStatus}
        completedSteps={rowData.completedSteps}
        totalSteps={rowData.totalSteps}
      />
    );
  };

  const progressBarJobStatus = (
    <Column
      field={'jobStatusName'}
      style={{ minWidth: '120px' }}
      bodyClassName={'jobs_status_column'}
      sortField={'jobStatusName'}
      sortable
      body={progressBarJobStatusTemplate}
      header={tc('JOBS_GRID_HEADER_JOB_STATUS')}
    />
  );

  return (
    <div className="jobs">
      {isLoading ? <Loader /> : ''}
      {isRedesignEnabled() && <div className="VLPageHeader" ref={pageHeaderRef}></div>}
      <div className="container-fluid">
        {!isRedesignEnabled() && (
          <>
            <div className="jobs-header">
              <div className="header-icon">
                <i className="icon-job-status" />
              </div>
              <h3 className="header-title">{tc('JOBS_TITLE')}</h3>
            </div>
            <hr className="divider" />
          </>
        )}
        <div className="jobs-search-bar">
          <div className="jobs-search-item">
            <div className="jobs-search-title">{tc('JOBS_SEARCH_JOB_STATUS')}</div>
            <div className="jobs-search-component">
              <select className="form-control" value={statusSelected} onChange={handleChangeStatus}>
                <option value="">{tc('JOBS_SEARCH_ALL_JOB_STATUSES')}</option>
                {statuses.length &&
                  statuses.map(status => (
                    <option value={status.name} key={status.name}>
                      {status.value}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="jobs-search-item">
            <div className="jobs-search-title">User:</div>
            <div className="jobs-search-component">
              <select className="form-control" value={selectedUser} onChange={handleChangeUser}>
                <option value="all">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="jobs-search-item">
            <div className="jobs-search-title">&nbsp;</div>
            <div className="jobs-search-buttons">
              <button className="btn btn-primary" onClick={handleSearch}>
                <span>{tc('JOBS_SEARCH_BUTTON')}</span>
              </button>
            </div>
          </div>
        </div>
        <hr />
        <div className="jobs-grid">
          <DataTablePM
            value={jobs}
            columns={columns}
            progressBarJobStatus={progressBarJobStatus}
            staticColumns={staticColumns}
            loading={isTableLoading}
            resizableColumns
            columnResizeMode="fit"
            sortMode="single"
            scrollable
            className="no-filter"
            rowClassName={rowClass}
          />
        </div>
      </div>

      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={errorMessage} />
      <DialogComponent open={showDetailsMessage} handleClose={closeDetailsMessage}>
        <div className="modal-job-details">
          <h3 className="modal-job-details-title">Job Details</h3>
          <div className="modal-job-details-body" dangerouslySetInnerHTML={{ __html: detailsMessage }}></div>
          {isFailureMessage && (
            <div className="modal-job-details-support">
              <span className="font-weight-500">
                Please try again in a few minutes.
                <br />
              </span>
              If the issue persists, contact <a href="mailto:support@centage.com">Centage Support</a>.
            </div>
          )}
          <div className="modal-job-details-footer">
            <button type="button" className="btn btn-primary ladda-button" onClick={closeDetailsMessage}>
              OK
            </button>
          </div>
        </div>
      </DialogComponent>
    </div>
  );
};

export default Jobs;
